import { observer } from 'mobx-react-lite';

import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { StockIndexResponse } from '../../../server/response/responseScheme';
import { apiClient } from '../../utils/apiClient';
import { useMobxInstance } from '../../utils/mobxUtil';
import ChangeValue from '../common/ChangeValue';
import ChangeBarGraph, { ChangeBarGraphItem } from './ChangeBarGraph';
import { useCommonNavigate } from '../common/CommonLink';
import dayjs from 'dayjs';
import TargetReturnButton from './TargetReturnButton';

class ViewStore {
    indexPerformance: StockIndexResponse[] = [];

    graphItems: ChangeBarGraphItem<{ indexTicker: string }>[] = [];
    briefingIndex: StockIndexResponse | null = null;
    briefDate: string | null = null;
    targetReturn: 'daily' | 'mtd' | 'ytd' = 'daily';

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        const response = await apiClient.stockIndex.public.getIndexPerformance.$query();

        runInAction(() => {
            this.indexPerformance = response;

            const lastDate = _.last(this.indexPerformance)?.price?.date;
            if (lastDate) {
                this.briefDate = lastDate;
            }
            this.makeGraphItems();
            this.makeBriefingIndex();
        });
    }

    makeGraphItems() {
        // 정렬된 데이터 캐시
        this.graphItems = _.map(this.indexPerformance, (item) => {
            const changePercent =
                this.targetReturn === 'daily'
                    ? Number(item.price?.dailyReturn) || 0
                    : this.targetReturn === 'mtd'
                      ? Number(item.price?.mtdReturn) || 0
                      : Number(item.price?.ytdReturn) || 0;

            return {
                name: item.nameKo || item.name,
                changePercent: changePercent,
                isBenchmark: item.ticker === '_SP500',
                data: {
                    indexTicker: item.ticker,
                },
            };
        });
    }

    private makeBriefingIndex() {
        this.briefingIndex =
            _.maxBy(this.indexPerformance, (item) => {
                return Math.abs(item.price?.dailyReturn || 0);
            }) || null;
    }

    changeTargetReturn(target: 'daily' | 'mtd' | 'ytd') {
        this.targetReturn = target;
        this.makeGraphItems();
    }

    get highlightPercent() {
        return this.targetReturn === 'daily' ? 1 : this.targetReturn === 'mtd' ? 3 : 10;
    }
}

const IndexBriefCard = observer(({ className }: { className?: string }) => {
    const viewStore = useMobxInstance(ViewStore);
    const { navigate } = useCommonNavigate();

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    return (
        <div className={twMerge('-mx-5 flex flex-col justify-around gap-3 py-4', className)}>
            <ChangeBarGraph<{ indexTicker: string }>
                items={viewStore.graphItems}
                highligthPercent={viewStore.highlightPercent}
                className="gap-3"
                onClickBar={(item) => {
                    navigate(`/daily-brief/index/${item.data?.indexTicker}`);
                }}
                isShowArrow={true}
            />
            {viewStore.briefingIndex && (
                <div className="mx-5 mt-4">
                    <div className="text-base-content/80 flex flex-col text-xl">
                        <div className="mt-5">
                            {viewStore.briefingIndex.nameKo || viewStore.briefingIndex.name} 지수가
                        </div>
                        <div className="mt-3">
                            <ChangeValue
                                className="mr-2 text-3xl font-semibold"
                                value={viewStore.briefingIndex.price?.dailyReturn || 0}
                                sign={true}
                                round={2}
                                suffix="%"
                            />
                            로
                        </div>
                        <div className="mt-3">
                            가장 많이{' '}
                            {viewStore.briefingIndex.price?.dailyReturn! > 0
                                ? '올랐습니다.'
                                : '내렸습니다.'}
                        </div>
                    </div>
                </div>
            )}
            {viewStore.briefDate && (
                <TargetReturnButton
                    className="mx-5 mt-8"
                    targetDate={viewStore.briefDate}
                    targetReturn={viewStore.targetReturn}
                    onChangeTargetReturn={(target) => viewStore.changeTargetReturn(target)}
                />
            )}
        </div>
    );
});

export default IndexBriefCard;
